import qs from 'qs';
import {
  BPExpirationReportResponse,
  DatesAtAGlanceReportResponse,
  NewClosedProjectsReportResponse,
  OverdueTasksReportResponse,
  PermittingOverviewReportResponse,
  ProjectDurationReportResponse,
  ReportsFilterOptionsResponse,
  ReportsFilters,
  StructuralPassFailReportResponse,
} from '@models/interfaces';
import { CommonHttpService } from './common-http.service';

const getReportsParams = (filters: ReportsFilters): ReportsFilters =>
  Object.entries(filters).reduce(
    (acc: ReportsFilters, [key, value]: [string, unknown]) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        return acc;
      }

      return {
        ...acc,
        [key]: value,
      };
    },
    {},
  );

const getReportsUrl = (
  apiUrl: string,
  path: string,
  params: ReportsFilters,
): string => {
  const queryString = qs.stringify(params, { arrayFormat: 'repeat' });

  return `${apiUrl}${path}${queryString ? `?${queryString}` : ''}`;
};

const getProjectReportParams = (filters: ReportsFilters): ReportsFilters => {
  const params = getReportsParams(filters);

  delete params.paceTask;
  delete params.paceTaskScreen;
  delete params.projectNumber;

  return params;
};

const getPermittingReportParams = (
  filters: ReportsFilters,
  overview: boolean,
): ReportsFilters => {
  const params = getProjectReportParams(filters);

  delete params.groupBy;
  delete params.actualDateFrom;
  delete params.actualDateTo;
  delete params.forecastedDateFrom;
  delete params.forecastedDateTo;
  delete params.milestoneDateFrom;
  delete params.milestoneDateTo;
  delete params.periodFrom;
  delete params.periodTo;

  if (!overview) {
    delete params.dateField;
    delete params.dateFrom;
    delete params.dateTo;
  }

  return params;
};

export class ReportsHttpService extends CommonHttpService {
  getPermittingOverview(
    filters: ReportsFilters,
  ): Promise<PermittingOverviewReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/permitting-overview',
        getPermittingReportParams(filters, true),
      ),
      null,
    );
  }

  getBPExpiration(
    filters: ReportsFilters,
  ): Promise<BPExpirationReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/bp-expiration',
        getPermittingReportParams(filters, false),
      ),
      null,
    );
  }

  getDatesAtAGlance(
    filters: ReportsFilters,
  ): Promise<DatesAtAGlanceReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/dates-at-a-glance',
        getReportsParams(filters),
      ),
      null,
    );
  }

  getFilterOptions(): Promise<ReportsFilterOptionsResponse> {
    return this.http.callApi.get(`${this.apiUrl}/reports/filter-options`);
  }

  getNewClosedProjects(
    filters: ReportsFilters,
  ): Promise<NewClosedProjectsReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/new-closed-projects',
        getProjectReportParams(filters),
      ),
      null,
    );
  }

  getOverdueTasks(
    filters: ReportsFilters,
  ): Promise<OverdueTasksReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/overdue-tasks',
        getReportsParams(filters),
      ),
      null,
    );
  }

  getProjectDuration(
    filters: ReportsFilters,
  ): Promise<ProjectDurationReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/project-duration',
        getProjectReportParams(filters),
      ),
      null,
    );
  }

  getStructuralPassFail(
    filters: ReportsFilters,
  ): Promise<StructuralPassFailReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/structural-pass-fail',
        getProjectReportParams(filters),
      ),
      null,
    );
  }
}
